import { Check, Lock, RefreshCw, RotateCcw, Timer, Undo2 } from "lucide-react"
import * as React from "react"

import { PageHeader } from "@/components/layout/page-header"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Notice } from "@/components/ui/notice"
import { Skeleton } from "@/components/ui/skeleton"
import { ApiError, getTimeouts, updateTimeouts } from "@/lib/api"
import type { TimeoutSetting, TimeoutSettings } from "@/lib/api"
import { formatDate, formatTime } from "@/lib/format"
import { cn } from "@/lib/utils"
import { SettingsTabs } from "@/pages/settings/tabs"

/**
 * Settings → Timeouts: сколько решение ждёт внешние проверки (AMEND-97 шаг 2).
 *
 * Эстетика — industrial-utilitarian «приборная панель», общая с соседними
 * вкладками Settings: шрифты, палитра и панели — из `index.css` и
 * `tailwind.config.ts`, экран, нарисованный иначе, читался бы как чужой
 * модуль. Своё здесь одно — шкала под каждым полем: риска умолчания, заливка
 * до значения и ромб-стрелка. Таймаут — это всегда «где между пределами», и
 * шкала отвечает на это раньше, чем глаз дочитает цифры подсказки. Вышли за
 * предел — ромб краснеет, ещё до нажатия «Сохранить».
 *
 * Поле — текстовое с `inputMode="decimal"`, а не `type="number"`: числовое
 * поле в браузере с английской локалью молча превращает «1,5» в пустую
 * строку, а здесь запятую наберут чаще точки. Шаг 0,1 — стрелками ↑/↓
 * (с Shift — 1 с), как у числового поля. Показываются числа с точкой — как
 * везде в консоли (`lib/format`, локаль `en-US`).
 *
 * Сохраняются только изменённые ключи; «Сбросить к умолчанию» уходит сразу и
 * одним ключом со значением `null`. После ответа экран рисует то, что вернул
 * сервер, а не набранное: значение могло лечь иначе (или его поправил сосед).
 * 403 — не админ: экран объясняет это словами, как журнал.
 */

/** Шаг стрелок в поле, секунды. */
const STEP = 0.1

const SECONDS = new Intl.NumberFormat("en-US", { maximumFractionDigits: 3, useGrouping: false })

function formatSeconds(value: number) {
  return SECONDS.format(value)
}

/** Число с точкой или запятой: `1.5`, `1,5`, `.5`, `2.`. Экспоненты и пробелы внутри — нет. */
const DECIMAL = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/

function parseSeconds(text: string): number | null {
  const normalized = text.trim().replace(",", ".")
  return DECIMAL.test(normalized) ? Number(normalized) : null
}

interface FieldCheck {
  value: number | null
  error: string | null
}

/** Проверка поля теми же пределами, что у ручки: `min` (строго или нет) и `max` включительно. */
function validate(item: TimeoutSetting, text: string): FieldCheck {
  if (text.trim() === "") return { value: null, error: "Введите число секунд." }
  const value = parseSeconds(text)
  if (value === null) return { value: null, error: "Не число: подойдёт 1.5 или 1,5." }
  if (item.min_exclusive ? value <= item.min : value < item.min) {
    return {
      value,
      error: item.min_exclusive
        ? `Должно быть больше ${formatSeconds(item.min)} с.`
        : `Не меньше ${formatSeconds(item.min)} с.`,
    }
  }
  if (value > item.max) return { value, error: `Не больше ${formatSeconds(item.max)} с.` }
  return { value, error: null }
}

/** Набранное расходится с действующим значением. «2.0» при значении 2 — не правка. */
function isDirty(item: TimeoutSetting, text: string) {
  const value = parseSeconds(text)
  return value === null || value !== item.value
}

function limitsHint(item: TimeoutSetting) {
  const min = formatSeconds(item.min)
  const max = formatSeconds(item.max)
  const limits = item.min_exclusive ? `пределы: больше ${min}, до ${max} с` : `пределы ${min}–${max} с`
  return `${limits} · по умолчанию ${formatSeconds(item.default)} с`
}

function draftsFrom(items: TimeoutSetting[]): Record<string, string> {
  return Object.fromEntries(items.map((item) => [item.key, formatSeconds(item.value)]))
}

const RU_PLURAL = new Intl.PluralRules("ru-RU")

function plural(count: number, one: string, few: string, many: string) {
  const form = RU_PLURAL.select(count)
  return `${count} ${form === "one" ? one : form === "few" ? few : many}`
}

/** Сервисы без повторов, в порядке строк: «enricher, port-checker». */
function servicesOf(items: TimeoutSetting[]) {
  return [...new Set(items.map((item) => item.service))].join(", ")
}

/**
 * Что сказать после успешной записи: сколько легло и когда заработает. «Живые»
 * подхватываются через кеш процесса, «после рестарта» — только перезапуском, и
 * смолчать об этом значило бы оставить человека ждать минуту, которой не хватит.
 */
function savedNote(changed: TimeoutSetting[], ttl: number, reset: boolean) {
  const restart = changed.filter((item) => item.applies === "restart")
  const live = changed.length - restart.length
  const head = reset
    ? `«${changed[0]?.label ?? ""}» сброшен к умолчанию — ${formatSeconds(changed[0]?.value ?? 0)} с.`
    : `Сохранено: ${plural(changed.length, "значение", "значения", "значений")}.`
  const parts = [head]
  if (live > 0) {
    parts.push(
      restart.length > 0
        ? `Живые процессы подхватят за ${formatSeconds(ttl)} с без рестарта.`
        : `Процессы подхватят за ${formatSeconds(ttl)} с без рестарта.`,
    )
  }
  if (restart.length > 0) parts.push(`Заработает после рестарта: ${servicesOf(restart)}.`)
  return parts.join(" ")
}

/**
 * Отказ ручки человеческой строкой. 422 с `detail`-строкой показываем как есть
 * — там сказано, что не так (например, таймаут enricher не выше бюджета). У
 * ошибки схемы FastAPI `detail` — список, и клиент оставляет голое «HTTP 422»:
 * код вместо объяснения заставил бы гадать.
 */
function describe(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.status === 422 && cause.message === "HTTP 422") {
      return "Сервер не принял значения — проверьте пределы и попробуйте ещё раз."
    }
    if (cause.status === 403 && cause.message === "HTTP 403") {
      return "Менять таймауты может только админ аккаунта."
    }
    return cause.message
  }
  return "Не удалось связаться с API."
}

export function TimeoutsPage() {
  const [data, setData] = React.useState<TimeoutSettings | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [loadError, setLoadError] = React.useState<Error | null>(null)
  const [reload, setReload] = React.useState(0)

  const [drafts, setDrafts] = React.useState<Record<string, string>>({})
  /** Поля, где ошибку уже пора показывать: покинутые или после попытки сохранить. */
  const [touched, setTouched] = React.useState<Record<string, boolean>>({})
  const [attempted, setAttempted] = React.useState(false)
  /** `"save"` — уходит форма, иначе ключ, который сбрасывается. */
  const [busy, setBusy] = React.useState<string | null>(null)
  const [saveError, setSaveError] = React.useState<string | null>(null)
  const [saved, setSaved] = React.useState<string | null>(null)

  const inputs = React.useRef<Record<string, HTMLInputElement | null>>({})

  React.useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    getTimeouts(controller.signal)
      .then((result) => {
        setData(result)
        setDrafts(draftsFrom(result.items))
        setTouched({})
        setAttempted(false)
        setLoadError(null)
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setData(null)
        setLoadError(cause instanceof Error ? cause : new Error("Request failed"))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [reload])

  const items = data?.items ?? []
  const rows = items.map((item) => {
    const draft = drafts[item.key] ?? formatSeconds(item.value)
    return { item, draft, dirty: isDirty(item, draft), ...validate(item, draft) }
  })
  const dirtyRows = rows.filter((row) => row.dirty)
  const forbidden = loadError instanceof ApiError && loadError.status === 403

  function change(key: string, text: string) {
    setDrafts((current) => ({ ...current, [key]: text }))
    setSaved(null)
    setSaveError(null)
  }

  function revert() {
    setDrafts(draftsFrom(items))
    setTouched({})
    setAttempted(false)
    setSaveError(null)
  }

  /**
   * Запись и разбор ответа. Черновики отправленных ключей заменяются тем, что
   * вернул сервер; чужие несохранённые правки (сброс одной строки посреди
   * правки другой) остаются как были — иначе сброс молча стёр бы набранное.
   */
  async function send(values: Record<string, number | null>, mode: string) {
    if (!data) return
    const before = data.items
    const sent = new Set(Object.keys(values))
    setBusy(mode)
    setSaveError(null)
    setSaved(null)
    try {
      const next = await updateTimeouts(values)
      setData(next)
      setDrafts((current) => {
        const merged: Record<string, string> = {}
        for (const item of next.items) {
          const old = before.find((row) => row.key === item.key)
          const text = current[item.key]
          const keep = !sent.has(item.key) && old && text !== undefined && isDirty(old, text)
          merged[item.key] = keep ? text : formatSeconds(item.value)
        }
        return merged
      })
      setTouched((current) =>
        Object.fromEntries(Object.entries(current).filter(([key]) => !sent.has(key))),
      )
      setAttempted(false)
      const changed = next.items.filter((item) => sent.has(item.key))
      setSaved(savedNote(changed, next.cache_ttl_seconds, mode !== "save"))
    } catch (cause: unknown) {
      setSaveError(describe(cause))
    } finally {
      setBusy(null)
    }
  }

  function submit(event: React.FormEvent) {
    event.preventDefault()
    if (busy !== null || dirtyRows.length === 0) return
    setAttempted(true)
    const invalid = dirtyRows.find((row) => row.error !== null)
    if (invalid) {
      // Запрос не уходит: сервер ответил бы 422 на то, что видно уже здесь.
      setSaved(null)
      setSaveError(null)
      inputs.current[invalid.item.key]?.focus()
      return
    }
    void send(
      Object.fromEntries(dirtyRows.map((row) => [row.item.key, row.value as number])),
      "save",
    )
  }

  const restartServices = servicesOf(items.filter((item) => item.applies === "restart"))

  return (
    <>
      <PageHeader title="Settings" tabs={<SettingsTabs />} />
      <div className="p-5">
        <div className="mx-auto max-w-4xl space-y-4">
          {forbidden ? (
            <NotAnAdmin message={loadError.message} />
          ) : loadError ? (
            <section className="animate-panel-in rounded border border-line bg-surface p-6 text-center shadow-panel">
              <p className="text-sm font-medium text-decline">{loadError.message}</p>
              <p className="mx-auto mt-1 max-w-md text-xs text-fg-muted">
                Поля намеренно не показаны: пустые или выдуманные значения читались бы как
                настоящие таймауты.
              </p>
              <Button className="mt-4" onClick={() => setReload((value) => value + 1)}>
                Повторить
              </Button>
            </section>
          ) : (
            <section className="animate-panel-in rounded border border-line bg-surface shadow-panel">
              <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line px-4 py-3">
                <div>
                  <span className="stencil-label text-fg-muted">Таймауты и бюджеты</span>
                  <h2 className="mt-0.5 text-sm font-semibold text-fg">
                    Сколько решение ждёт внешние проверки
                  </h2>
                </div>
                <span className="max-w-md text-xs text-fg-subtle">
                  Значение отсюда главнее env; без правки действует умолчание из env. Пределы —
                  жёсткие: шире них сервис значение не примет.
                </span>
              </header>

              {data && (
                <p className="flex items-start gap-2 border-b border-line bg-surface-sunken px-4 py-2 text-xs leading-relaxed text-fg-muted">
                  <Timer className="mt-px h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                  <span>
                    Правка подхватывается без рестарта — в течение{" "}
                    <span className="font-medium text-fg">
                      {formatSeconds(data.cache_ttl_seconds)} с
                    </span>
                    , пока у процессов не истечёт кеш.
                    {restartServices && (
                      <>
                        {" "}
                        Кроме полей с пометкой «после рестарта»: их сервис ({restartServices})
                        читает значение при старте — правка ляжет в базу сразу, а заработает после
                        перезапуска.
                      </>
                    )}
                  </span>
                </p>
              )}

              {loading && !data ? (
                <ListSkeleton />
              ) : items.length === 0 ? (
                <p className="px-6 py-10 text-center text-xs text-fg-muted">
                  Ручка не вернула ни одного таймаута.
                </p>
              ) : (
                <form onSubmit={submit} noValidate>
                  <div className="hidden grid-cols-[minmax(0,1fr)_21rem] gap-x-6 border-b border-line-strong bg-surface-sunken px-4 py-2 lg:grid">
                    <span className="stencil-label">Параметр</span>
                    <span className="stencil-label">Значение</span>
                  </div>
                  <ul>
                    {rows.map((row, index) => (
                      <TimeoutRow
                        key={row.item.key}
                        index={index}
                        item={row.item}
                        draft={row.draft}
                        dirty={row.dirty}
                        value={row.value}
                        error={
                          row.dirty && (touched[row.item.key] || attempted) ? row.error : null
                        }
                        disabled={busy !== null}
                        resetting={busy === row.item.key}
                        inputRef={(element) => {
                          inputs.current[row.item.key] = element
                        }}
                        onChange={(text) => change(row.item.key, text)}
                        onBlur={() =>
                          setTouched((current) => ({ ...current, [row.item.key]: true }))
                        }
                        onReset={() => void send({ [row.item.key]: null }, row.item.key)}
                      />
                    ))}
                  </ul>

                  {/* Подвал липнет к низу окна: «Сохранить» и ответ сервера видны,
                      в какой бы строке ни шла правка. */}
                  <footer className="sticky bottom-0 z-10 flex flex-wrap items-center gap-3 rounded-b border-t border-line bg-surface px-4 py-3">
                    <span className="text-xs text-fg-muted" aria-live="polite">
                      {dirtyRows.length > 0
                        ? `Не сохранено: ${plural(dirtyRows.length, "поле", "поля", "полей")}`
                        : "Изменений нет"}
                    </span>
                    <div className="ml-auto flex items-center gap-2">
                      {dirtyRows.length > 0 && (
                        <Button type="button" variant="ghost" disabled={busy !== null} onClick={revert}>
                          <Undo2 className="h-3.5 w-3.5" strokeWidth={2} />
                          Отменить
                        </Button>
                      )}
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={dirtyRows.length === 0 || busy !== null}
                      >
                        {busy === "save" ? "Сохраняем…" : "Сохранить"}
                      </Button>
                    </div>
                    {saveError && (
                      <Notice tone="error" className="w-full">
                        {saveError}
                      </Notice>
                    )}
                    {saved && (
                      <Notice tone="ok" icon={Check} className="w-full">
                        {saved}
                      </Notice>
                    )}
                  </footer>
                </form>
              )}
            </section>
          )}
        </div>
      </div>
    </>
  )
}

// ── строка ───────────────────────────────────────────────────────────────────

function TimeoutRow({
  index,
  item,
  draft,
  dirty,
  value,
  error,
  disabled,
  resetting,
  inputRef,
  onChange,
  onBlur,
  onReset,
}: {
  index: number
  item: TimeoutSetting
  draft: string
  dirty: boolean
  value: number | null
  error: string | null
  disabled: boolean
  resetting: boolean
  inputRef: (element: HTMLInputElement | null) => void
  onChange: (text: string) => void
  onBlur: () => void
  onReset: () => void
}) {
  const id = `timeout-${item.key}`
  const outOfRange = value !== null && validate(item, draft).error !== null

  /** Шаг стрелками, как у числового поля; пределы не перешагиваем. */
  function step(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return
    event.preventDefault()
    const size = event.shiftKey ? 1 : STEP
    const current = parseSeconds(draft) ?? item.value
    let next = Math.round((current + (event.key === "ArrowUp" ? size : -size)) * 1000) / 1000
    if (next > item.max) next = item.max
    if (next < item.min) next = item.min
    if (item.min_exclusive && next <= item.min) return
    onChange(formatSeconds(next))
  }

  return (
    <li
      className={cn(
        "relative grid animate-row-in gap-x-6 gap-y-3 border-b border-line px-4 py-4 transition-colors duration-150 ease-snap lg:grid-cols-[minmax(0,1fr)_21rem]",
        dirty && "bg-accent-soft/40",
      )}
      style={{ animationDelay: `${Math.min(index, 12) * 25}ms` }}
    >
      {/* Несохранённая строка — с риской акцента слева: «Сохранить» одна на
          форму, и глаз должен найти, что именно она отправит. */}
      <span
        aria-hidden
        className={cn(
          "absolute inset-y-0 left-0 w-0.5 bg-accent transition-opacity duration-150",
          dirty ? "opacity-100" : "opacity-0",
        )}
      />

      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor={id} className="text-sm font-medium text-fg">
            {item.label}
          </label>
          <Badge>{item.service}</Badge>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-fg-muted">{item.description}</p>
        <p className="mt-1 break-all font-mono text-[11px] text-fg-subtle">{item.key}</p>

        {(item.overridden || item.applies === "restart") && (
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5">
            {item.overridden && (
              <>
                <Badge tone="accent">изменено</Badge>
                {(item.updated_by || item.updated_at) && (
                  <span className="text-xs text-fg-subtle">
                    {[
                      item.updated_by,
                      item.updated_at
                        ? `${formatDate(item.updated_at)} ${formatTime(item.updated_at)}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                )}
              </>
            )}
            {item.applies === "restart" && (
              <Badge tone="review">
                <RefreshCw className="h-3 w-3" strokeWidth={2.25} />
                после рестарта {item.service}
              </Badge>
            )}
          </div>
        )}
      </div>

      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-28">
            <Input
              ref={inputRef}
              id={id}
              name={item.key}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              spellCheck={false}
              value={draft}
              disabled={disabled}
              aria-invalid={error !== null}
              aria-describedby={error ? `${id}-hint ${id}-error` : `${id}-hint`}
              onChange={(event) => onChange(event.target.value)}
              onBlur={onBlur}
              onKeyDown={step}
              className={cn(
                "pr-7 text-right font-medium tabular-nums",
                error && "border-decline focus:border-decline",
              )}
            />
            <span
              aria-hidden
              className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-xs text-fg-muted"
            >
              с
            </span>
          </div>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={!item.overridden || disabled}
            onClick={onReset}
            aria-label={`Сбросить к умолчанию: ${item.label}`}
          >
            <RotateCcw className={cn("h-3.5 w-3.5", resetting && "animate-spin")} strokeWidth={2} />
            Сбросить к умолчанию
          </Button>
        </div>

        <Scale item={item} value={value} outOfRange={outOfRange} />

        <p id={`${id}-hint`} className="mt-1.5 text-xs text-fg-muted">
          {limitsHint(item)}
        </p>
        {error && (
          <p id={`${id}-error`} className="mt-1 text-xs font-medium text-decline">
            {error}
          </p>
        )}
      </div>
    </li>
  )
}

/**
 * Шкала поля: от `min` до `max`, риска — умолчание, заливка и ромб — набранное
 * значение. Только для глаз (`aria-hidden`): те же числа поле объявляет
 * подсказкой. Не число — заливки и ромба нет: показывать «где-то тут» нечем.
 */
function Scale({
  item,
  value,
  outOfRange,
}: {
  item: TimeoutSetting
  value: number | null
  outOfRange: boolean
}) {
  const span = item.max - item.min
  const at = (point: number) =>
    span > 0 ? Math.min(100, Math.max(0, ((point - item.min) / span) * 100)) : 0

  return (
    <div aria-hidden className="relative mt-3 h-2.5 w-full max-w-[19rem]">
      <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-line-strong" />
      <div className="absolute left-0 top-0 h-full w-px bg-line-strong" />
      <div className="absolute right-0 top-0 h-full w-px bg-line-strong" />
      <div
        className="absolute top-0 h-full w-px bg-fg-subtle"
        style={{ left: `${at(item.default)}%` }}
      />
      {value !== null && (
        <>
          <div
            className={cn(
              "absolute left-0 top-1/2 h-[3px] -translate-y-1/2 transition-[width] duration-200 ease-snap",
              outOfRange ? "bg-decline/50" : "bg-accent/45",
            )}
            style={{ width: `${at(value)}%` }}
          />
          <div
            className={cn(
              "absolute top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45 transition-[left,background-color] duration-200 ease-snap",
              outOfRange ? "bg-decline" : "bg-accent",
            )}
            style={{ left: `${at(value)}%` }}
          />
        </>
      )}
    </div>
  )
}

// ── состояния ────────────────────────────────────────────────────────────────

/** Вошедший — не админ. Объясняем, а не показываем пустоту. */
function NotAnAdmin({ message }: { message: string }) {
  return (
    <section className="animate-panel-in rounded border border-line bg-surface p-6 shadow-panel">
      <div className="flex items-center gap-2 text-fg-muted">
        <Lock className="h-4 w-4" strokeWidth={1.75} />
        <span className="stencil-label">Только для админа</span>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-fg">{message}</p>
      <p className="mt-2 text-xs leading-relaxed text-fg-muted">
        Ваш доступ работает как обычно — закрыта только эта вкладка. Таймауты решают, сколько
        решение ждёт внешние проверки, поэтому их меняет админ.
      </p>
    </section>
  )
}

function ListSkeleton() {
  return (
    <div className="space-y-3 p-4">
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} className="h-14 w-full" />
      ))}
    </div>
  )
}
